import { ArrowRight2 } from "iconsax-reactjs"
import React, { ReactNode, useEffect, useRef, useState } from "react"

import { cn } from "@/modules/helpers/hooks/shared/utils"

export interface ToggleFilterOption<T = string> {
  value: T
  label: string
  icon?: ReactNode
  iconColor?: string
  count?: number
  textClassName?: string
  containerClassName?: string
}

interface ToggleFilterProps<T = string> {
  options: ToggleFilterOption<T>[]
  value: T
  onChange: (value: T) => void
  className?: string
  defaultValue?: T
  isToggle?: boolean
}

export function ToggleFilter<T = string>({
  options,
  value,
  onChange,
  className,
  defaultValue,
  isToggle = false
}: ToggleFilterProps<T>) {
  // All hooks must be called unconditionally before any early return
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [showScrollIndicator, setShowScrollIndicator] = useState(false)

  useEffect(() => {
    // Only run scroll logic when not in toggle mode
    if (isToggle) return

    const checkScroll = () => {
      if (scrollContainerRef.current && options.length >= 3) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current
        const canScrollMore = scrollLeft + clientWidth < scrollWidth - 10
        setShowScrollIndicator(canScrollMore)
      }
    }

    checkScroll()
    const scrollContainer = scrollContainerRef.current
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", checkScroll)
      window.addEventListener("resize", checkScroll)
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", checkScroll)
      }
      window.removeEventListener("resize", checkScroll)
    }
  }, [options.length, isToggle])

  const isActive = (optionValue: T, index: number) => {
    if (value) {
      return value === optionValue
    }
    // If no value selected, use defaultValue or first option
    return defaultValue ? defaultValue === optionValue : index === 0
  }

  const getIconColor = (optionValue: T, index: number, iconColor?: string) => {
    if (isActive(optionValue, index)) {
      return "#fff"
    }
    return iconColor || "#045664"
  }

  // Toggle mode - optimized for 2 options with gradient background
  if (isToggle) {
    return (
      <div
        className={cn("flex items-center gap-2 mb-2 w-full border-b border-primary-50", className)}
      >
        {options.map((option, index) => (
          <div
            key={String(option.value)}
            onClick={() => onChange(option.value)}
            className={cn(
              "flex-1 flex items-center justify-center gap-1 px-4 py-2 rounded-t-3xl cursor-pointer transition-all",
              {
                "bg-gradient-primary !text-white": isActive(option.value, index),
                [`${option?.textClassName}`]: option?.textClassName,
                [`${option?.containerClassName}`]: option?.containerClassName
              }
            )}
          >
            {option.icon && (
              <span className="flex items-center">
                {React.isValidElement(option.icon)
                  ? React.cloneElement(option.icon as React.ReactElement<any>, {
                      size: 18,
                      color: getIconColor(option.value, index, option.iconColor)
                    })
                  : option.icon}
              </span>
            )}
            <p
              className={cn("text-sm font-light text-black whitespace-nowrap", {
                "text-white": isActive(option.value, index),
                [`${option?.textClassName}`]: option?.textClassName
              })}
            >
              {option.label}
            </p>
          </div>
        ))}
      </div>
    )
  }

  // Tabs mode - support many options with scrollable tabs (same UI as toggle)
  return (
    <div className={cn("relative w-full", className)}>
      <div
        ref={scrollContainerRef}
        className="flex items-center gap-2 w-full border-b border-primary-50 overflow-x-auto scrollbar-hide"
      >
        {options.map((option, index) => (
          <div
            key={String(option.value)}
            onClick={() => onChange(option.value)}
            className={cn(
              "flex items-center justify-center gap-1 px-3 py-2 rounded-t-2xl cursor-pointer transition-all whitespace-nowrap",
              {
                "bg-gradient-primary !text-white":
                  isActive(option.value, index) && !option?.containerClassName,
                [`${option?.textClassName}`]: option?.textClassName,
                [`${option?.containerClassName}`]:
                  option?.containerClassName && isActive(option.value, index)
              }
            )}
          >
            {option.icon && (
              <span className="flex items-center">
                {React.isValidElement(option.icon)
                  ? React.cloneElement(option.icon as React.ReactElement<any>, {
                      size: 16,
                      color: getIconColor(option.value, index, option.iconColor)
                    })
                  : option.icon}
              </span>
            )}
            <p
              className={cn("text-xs font-light text-black whitespace-nowrap", {
                "text-white": isActive(option.value, index),
                [`${option?.textClassName}`]: option?.textClassName
              })}
            >
              {option.label}
            </p>
            {option.count !== undefined && (
              <span
                className={cn(
                  "text-[10px] text-white bg-primary-800/30 min-w-5 h-5 px-1 flex justify-center items-center rounded-sm font-medium",
                  {
                    "bg-white/20": isActive(option.value, index)
                  }
                )}
              >
                {option.count}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Scroll Indicator Arrow */}
      {showScrollIndicator && (
        <div className="absolute -right-1 top-0 bottom-0 flex items-center pointer-events-none">
          <div className="bg-gradient-to-l from-white via-white to-transparent pl-8 pr-2 h-full flex items-center">
            <ArrowRight2 size={16} className="text-primary animate-pulse" />
          </div>
        </div>
      )}
    </div>
  )
}
